import React from "react";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import { ImgTimeTaken } from "./Images/ImgTimeTaken.tsx";
import { formatTokenCount } from "../utils/format.ts";
import type { Message } from "../models.ts";

export interface MessageStatsProps {
  message: Message;
  isGenerating?: boolean;
}

@observer
export class MessageStats extends React.Component<MessageStatsProps> {
  @observable private _showTimeTakenPopup: boolean = false;
  @observable private _popupHiding: boolean = false;
  @observable private _now: number = Date.now();
  private _intervalId: ReturnType<typeof setInterval> | null = null;
  private _hideTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: MessageStatsProps) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    document.addEventListener('click', this._onDocumentClickTimeTaken);
    this._syncInterval();
  }

  componentDidUpdate(prevProps: MessageStatsProps) {
    if (prevProps.isGenerating !== this.props.isGenerating) {
      this._syncInterval();
    }
  }

  componentWillUnmount(): void {
    document.removeEventListener('click', this._onDocumentClickTimeTaken);
    if (this._intervalId !== null) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
    if (this._hideTimer) clearTimeout(this._hideTimer);
  }

  private _syncInterval() {
    if (this.props.isGenerating) {
      if (this._intervalId === null) {
        this._intervalId = setInterval(action(() => { this._now = Date.now(); }), 100);
      }
    } else {
      if (this._intervalId !== null) {
        clearInterval(this._intervalId);
        this._intervalId = null;
      }
    }
  }

  render() {
    const { message, isGenerating } = this.props;

    // Preceding time: accumulated tool execution durations from prior iterations.
    // Skipped for collapsed messages because their timeTaken already sums all contributors.
    const precedingMs = (!message.collapsedMessages && message._precedingMs) ? message._precedingMs : 0;

    // Current LLM call time (live counter or Ollama's elapsed_ms on done).
    let currentCallSec: number;
    if (isGenerating && message._fetchStartMs !== undefined) {
      currentCallSec = (this._now - message._fetchStartMs) / 1000;
    } else {
      currentCallSec = (message.timeTaken || 0) / 1_000_000_000;
    }

    // Total time shown in badge = current call + all prior iterations
    const timeSec = currentCallSec + precedingMs / 1000;

    // Thinking time: live during thinking phase, fixed once done.
    let thinkSec: number | null;
    if (isGenerating && message._thinkingStartMs !== undefined && message.thinkingTimeTaken == null) {
      thinkSec = (this._now - message._thinkingStartMs) / 1000;
    } else {
      thinkSec = message.thinkingTimeTaken != null ? message.thinkingTimeTaken / 1000 : null;
    }

    // TPS/genSec: based on current call only (tokens belong to this LLM call)
    const genSec = thinkSec != null ? Math.max(0, currentCallSec - thinkSec) : currentCallSec;
    const tps = (message.tokensOut && genSec > 0) ? (message.tokensOut / genSec) : null;

    return (
      <span className="message-time-taken message-time-taken--clickable" onClick={this._onTimeTakenClick}>
        <ImgTimeTaken size={12} /> {timeSec.toFixed(1)}s
        {(this._showTimeTakenPopup || this._popupHiding) && (
          <div className={`tc-popup tt-popup${this._popupHiding ? ' tc-popup--hiding' : ''}`} onClick={e => e.stopPropagation()}>
            <div className="tc-popup-title">Response stats</div>
            {tps != null && (
              <div className="tc-popup-row">
                <span className="tc-popup-label">Tokens / sec</span>
                <span className="tc-popup-value tt-value-highlight">{tps.toFixed(1)}</span>
              </div>
            )}
            <div className="tc-popup-row">
              <span className="tc-popup-label">Total time</span>
              <span className="tc-popup-value">{timeSec.toFixed(2)}s</span>
            </div>
            {precedingMs > 0 && (
              <div className="tc-popup-row">
                <span className="tc-popup-label">LLM time</span>
                <span className="tc-popup-value">{currentCallSec.toFixed(2)}s</span>
              </div>
            )}
            {thinkSec != null && (
              <div className="tc-popup-row">
                <span className="tc-popup-label">Thinking time</span>
                <span className="tc-popup-value">{thinkSec.toFixed(2)}s</span>
              </div>
            )}
            {thinkSec != null && (
              <div className="tc-popup-row">
                <span className="tc-popup-label">Generation time</span>
                <span className="tc-popup-value">{genSec.toFixed(2)}s</span>
              </div>
            )}
            {message.tokensIn != null && (
              <div className="tc-popup-row">
                <span className="tc-popup-label">Tokens in</span>
                <span className="tc-popup-value">{formatTokenCount(message.tokensIn)}</span>
              </div>
            )}
            {message.tokensOut != null && (
              <div className="tc-popup-row">
                <span className="tc-popup-label">Tokens out</span>
                <span className="tc-popup-value">{formatTokenCount(message.tokensOut)}</span>
              </div>
            )}
            {message.tokensIn != null && message.tokensOut != null && (
              <div className="tc-popup-row">
                <span className="tc-popup-label">Total tokens</span>
                <span className="tc-popup-value">{formatTokenCount(message.tokensIn + message.tokensOut)}</span>
              </div>
            )}
          </div>
        )}
      </span>
    );
  }
  
  private _hidePopup = () => {
    if (!this._showTimeTakenPopup) return;
    if (this._hideTimer) clearTimeout(this._hideTimer);
    this._popupHiding = true;
    // Let the tc-popup-out animation finish before unmounting.
    this._hideTimer = setTimeout(() => {
      this._showTimeTakenPopup = false;
      this._popupHiding = false;
      this._hideTimer = null;
    }, 150);
  }

  private _onTimeTakenClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (this._showTimeTakenPopup) {
      this._hidePopup();
    } else {
      if (this._hideTimer) { clearTimeout(this._hideTimer); this._hideTimer = null; }
      this._popupHiding = false;
      this._showTimeTakenPopup = true;
    }
  }

  private _onDocumentClickTimeTaken = () => {
    this._hidePopup();
  }
}
